// foodmenu/menu.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import RenderTableMenu from "./renderTableMenu";
import EditMenu from "./editMenu";
import MenuAPI from "../../services/API/menu";
import { useNotification } from "../../context/NotificationContext";
import ConfirmDialog from "../../components/ConfirmDialog";

const ITEMS_PER_PAGE = 10;

export default function Menu() {
  const { showNotification } = useNotification();
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await MenuAPI.getItems();
      setAllItems(res.data);
    } catch (err) {
      showNotification("Failed to load menu items", "error");
    }
  }, [showNotification]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // inside Menu component
  const handleAdd = () => {
    setItemToEdit(null); // ensure form is empty
    setIsFormVisible(true);
  };

  const paginatedItems = useMemo(() => {
    let tempItems = [...allItems];
    if (searchTerm) {
      tempItems = tempItems.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.price.toString().includes(searchTerm)
      );
    }
    setFilteredItems(tempItems);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return tempItems.slice(start, end);
  }, [allItems, searchTerm, currentPage]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleEdit = (item) => {
    setItemToEdit(item);
    setIsFormVisible(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await MenuAPI.deleteItem(deleteId);
      fetchItems();
      showNotification("Item deleted successfully", "success");
    } catch (err) {
      showNotification("Failed to delete item", "error");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const handleFormSubmit = async (payload) => {
    try {
      if (payload._id) {
        await MenuAPI.updateItem(payload._id, payload);
        showNotification("Menu item updated successfully", "success");
      } else {
        await MenuAPI.addItem(payload);
        showNotification("Menu item added successfully", "success");
      }
      setIsFormVisible(false);
      setItemToEdit(null);
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.message || "Menu item already exists.";

      showNotification(msg, "error");
      console.error("Menu API error:", err);
    }
  };

  return (
    <section id="menu-section" className="content-body">
      {isFormVisible && (
        <EditMenu
          item={itemToEdit}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormVisible(false)}
        />
      )}
      <RenderTableMenu
        paginatedItems={paginatedItems}
        allItems={allItems}
        onSearch={handleSearch}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={isLoading}
        onAdd={handleAdd}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this menu item?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </section>
  );
}
